import React, { useState, useRef } from 'react';
import Modal from 'react-modal';
import Input from '../Input/Input';
import Button from '../Button/Button';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './PreviousLandlordModal.css';

const PreviousLandlordModal = ({ isOpen, onClose, onSave, initialData = null, viewOnly = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
  });
  const [errors, setErrors] = useState({});
  const phoneNumberRef = useRef(null);

  // When opening with initialData (e.g. view saved form), prefill and optionally lock
  React.useEffect(() => {
    if (isOpen && initialData) {
      setFormData({
        name: initialData.name || '',
        phoneNumber: initialData.phoneNumber || '',
        email: initialData.email || '',
      });
    }
    if (isOpen && !initialData && !viewOnly) {
      setFormData({ name: '', phoneNumber: '', email: '' });
    }
  }, [isOpen, initialData, viewOnly]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, phoneNumber: value }));
    if (errors.phoneNumber) {
      setErrors((prev) => ({ ...prev, phoneNumber: '' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Previous Landlord Name is required';
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone Number is required';
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
    // Reset form
    setFormData({ name: '', phoneNumber: '', email: '' });
    setErrors({});
  };

  const handleClose = () => {
    setFormData({ name: '', phoneNumber: '', email: '' });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Previous Landlord Form"
      shouldCloseOnOverlayClick={true}
      ariaHideApp={false}
      className="previous-landlord-modal"
      overlayClassName="previous-landlord-modal-overlay"
    >
      <button className="previous-landlord-modal-close" onClick={handleClose}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <h2 className="previous-landlord-modal-title">Previous Landlord</h2>
      <form onSubmit={handleSubmit} className="previous-landlord-form">
        <Input
          type="text"
          name="name"
          label="Previous Landlord Name"
          placeholder="Enter landlord name"
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          required
          disabled={viewOnly}
        />
        <div className="previous-landlord-phone-field">
          <label className="previous-landlord-phone-label">
            Phone Number
            <span className="required-asterisk">*</span>
          </label>
          <PhoneInput
            country={'us'}
            value={formData.phoneNumber}
            onChange={handlePhoneChange}
            countryCodeEditable={false}
            inputClass={`previous-landlord-phone-input ${errors.phoneNumber ? 'input-error' : ''} ${viewOnly ? 'read-only' : ''}`}
            containerClass="previous-landlord-phone-input-container"
            buttonClass="previous-landlord-phone-country-button"
            dropdownClass="previous-landlord-phone-country-dropdown"
            enableSearch
            searchPlaceholder="Search country"
            placeholder="Enter phone number"
            inputProps={{
              name: 'phoneNumber',
              ref: phoneNumberRef,
              readOnly: viewOnly,
              disabled: viewOnly,
            }}
          />
          {errors.phoneNumber && (
            <div className="error-message" style={{ marginTop: '0.25rem' }}>
              {errors.phoneNumber}
            </div>
          )}
        </div>
        <Input
          type="email"
          name="email"
          label="Email (Optional)"
          placeholder="Enter email address"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          disabled={viewOnly}
        />
        {viewOnly ? (
          <Button
            type="button"
            variant="success"
            className="previous-landlord-save-button"
            onClick={handleClose}
          >
            Close
          </Button>
        ) : (
          <Button
            type="submit"
            variant="success"
            className="previous-landlord-save-button"
          >
            Save
          </Button>
        )}
      </form>
    </Modal>
  );
};

export default PreviousLandlordModal;
